import * as THREE from 'three';
import {
  MAX_QUEUE_LINE_SHOPPERS,
  MAX_QUEUE_LINES,
  queueLineColorMap,
  SELECTION_COLOR,
  SELECTION_OPACITY,
} from './constants';
import {
  addSelectionOverlay,
  createShopperMesh,
  isShopperOverlapping,
  updateShopperBadgeSelection,
  updateShopperOrderBadge,
} from './meshOperations';
import {
  isShopperMesh,
  QueueLineModel,
  ShopperMesh,
  ShopperType,
} from './types';

// Initialize queue lines
export const initializeQueueLines = (): QueueLineModel[] => {
  return Array.from({ length: MAX_QUEUE_LINES }, (_, index) => ({
    lineId: index + 1,
    color: queueLineColorMap[(index + 1) as keyof typeof queueLineColorMap],
    shoppers: [],
  }));
};

// Find the first available queue line for new shoppers
export const getAvailableQueueLine = (
  queueLines: QueueLineModel[]
): QueueLineModel | null => {
  // Find the first empty queue line (with no shoppers)
  for (const line of queueLines) {
    if (line.shoppers.length === 0) {
      return line;
    }
  }
  return null;
};

// Add a shopper to a queue line
export const addShopperToQueueLine = (
  scene: THREE.Scene,
  position: THREE.Vector3,
  currentQueueLine: QueueLineModel,
  shopperType: ShopperType
): ShopperMesh | null => {
  if (currentQueueLine.shoppers.length >= MAX_QUEUE_LINE_SHOPPERS) {
    return null;
  }

  // Create the new shopper
  const newOrderInLine = currentQueueLine.shoppers.length + 1;
  const shopperMesh = createShopperMesh(
    position,
    shopperType,
    newOrderInLine,
    currentQueueLine.lineId,
    currentQueueLine.color,
    0, // default rotation
    scene // pass scene for badge container
  );

  // Add to scene
  scene.add(shopperMesh);

  return shopperMesh;
};

// Check for overlapping shoppers
export const checkForOverlappingShoppers = (scene: THREE.Scene): string[] => {
  const shoppers = scene.children.filter(
    (obj): obj is ShopperMesh =>
      obj instanceof THREE.Mesh && obj.userData.isQueueLineShopper === true
  );

  const allObjects = scene.children.filter(
    (obj) => obj instanceof THREE.Mesh && obj.userData.isInteractive === true
  );

  const overlappingShopperIds: string[] = [];

  shoppers.forEach((shopper) => {
    if (isShopperOverlapping(shopper, allObjects as THREE.Mesh[])) {
      overlappingShopperIds.push(shopper.id.toString());
    }
  });

  return overlappingShopperIds;
};

// Update shopper types
export const updateShopperType = (
  scene: THREE.Scene,
  shopper: ShopperMesh,
  newType: ShopperType
): void => {
  if (!isShopperMesh(shopper)) {
    console.error('Invalid shopper mesh provided to updateShopperType');
    return;
  }

  // Store the shopper's position and properties
  const position = shopper.position.clone();
  const orderInLine = shopper.userData.orderInLine;
  const lineId = shopper.userData.lineId;
  const color = shopper.userData.color;
  const rotation = shopper.rotation.z;
  const badgeContainer = shopper.userData.badgeContainer;

  // Remove the old shopper from the scene
  scene.remove(shopper);
  if (badgeContainer) {
    scene.remove(badgeContainer);
  }

  // Create a new shopper with the updated type
  const newShopper = createShopperMesh(
    position,
    newType,
    orderInLine,
    lineId,
    color,
    rotation,
    scene
  );

  // Add the new shopper to the scene
  scene.add(newShopper);

  // If the shopper was selected, add the selection overlay
  if (shopper.userData.hasOverlay) {
    addSelectionOverlay(newShopper, scene, SELECTION_COLOR, SELECTION_OPACITY);
    updateShopperBadgeSelection(newShopper, true);
  }
};

// Check if all shoppers have been deleted from the scene
export const areAllShoppersDeleted = (scene: THREE.Scene): boolean => {
  const shoppers = scene.children.filter(
    (obj) => obj instanceof THREE.Mesh && obj.userData.isQueueLineShopper
  );
  console.log('areAllShoppersDeleted - shoppers count:', shoppers.length);
  return shoppers.length === 0;
};

// Remove a shopper and resequence the queue line
export const removeShopper = (
  scene: THREE.Scene,
  shopper: ShopperMesh,
  queueLines: QueueLineModel[]
): boolean => {
  console.log(
    'removeShopper called with shopper:',
    shopper.id,
    shopper.userData
  );

  // Remove the selection overlay if it exists
  if (shopper.userData.hasOverlay) {
    const overlayUuid = shopper.userData.hasOverlay;
    const overlay = scene.children.find((obj) => obj.uuid === overlayUuid);
    if (overlay) {
      scene.remove(overlay);
    }
  }

  // Remove the badge container from the scene if it exists
  if (shopper.userData.badgeContainer) {
    console.log('Removing badge container');
    scene.remove(shopper.userData.badgeContainer);
  }

  // Remove the shopper from the scene
  console.log('Removing shopper from scene');
  scene.remove(shopper);

  // Get all remaining shoppers in this queue line
  const remainingShoppers = scene.children
    .filter(
      (obj): obj is ShopperMesh =>
        obj instanceof THREE.Mesh &&
        obj.userData.isQueueLineShopper &&
        obj.userData.lineId === shopper.userData.lineId
    )
    .sort((a, b) => a.userData.orderInLine - b.userData.orderInLine);

  console.log('Remaining shoppers:', remainingShoppers.length);

  // Resequence the remaining shoppers
  remainingShoppers.forEach((shopperMesh, index) => {
    const newOrderInLine = index + 1;
    if (shopperMesh.userData.orderInLine !== newOrderInLine) {
      shopperMesh.userData.orderInLine = newOrderInLine;
      updateShopperOrderBadge(shopperMesh, newOrderInLine);
    }
  });

  // Update the queue line model if found
  const lineIndex = queueLines.findIndex(
    (line) => line.lineId === shopper.userData.lineId
  );
  if (lineIndex !== -1) {
    queueLines[lineIndex].shoppers = remainingShoppers;
  }

  // Check if all shoppers have been deleted
  const allDeleted = areAllShoppersDeleted(scene);
  console.log('All shoppers deleted:', allDeleted);

  return allDeleted;
};

// Find the queue line a shopper belongs to
export const getShopperQueueLine = (
  queueLines: QueueLineModel[],
  shopper: ShopperMesh
): QueueLineModel | undefined => {
  return queueLines.find((line) => line.lineId === shopper.userData.lineId);
};

// Update the elapsed wait time for a shopper
export const updateShopperWaitTime = (
  shopper: ShopperMesh,
  elapsedWaitTime: string
): void => {
  shopper.userData.elapsedWaitTime = elapsedWaitTime;
};
